import {
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import {
  axisProps,
  chart,
  formatMinutes,
  sentimentRating,
  sentimentScale,
} from "./theme";

// Busiest and lowest-sentiment agents get a name label; the rest rely on the
// tooltip and table so the plot doesn't drown in text.
const labelled = (agents) => {
  const byCalls = [...agents].sort((a, b) => b.calls - a.calls).slice(0, 2);
  const bySentiment = [...agents]
    .sort((a, b) => a.avg_sentiment - b.avg_sentiment)
    .slice(0, 2);
  return new Set([...byCalls, ...bySentiment].map((a) => a.id));
};

function AgentScatter({ agents, averageSentiment, style }) {
  const named = labelled(agents);
  const data = agents.map((a) => ({
    ...a,
    label: named.has(a.id) ? a.name.split(" ")[0] : "",
  }));
  const avgCalls =
    agents.reduce((s, a) => s + a.calls, 0) / (agents.length || 1);

  return (
    <ChartCard
      title="Agent performance"
      subtitle="Call volume against average sentiment. Bubble size is talk time"
      height={320}
      style={style}
      table={{
        columns: ["Agent", "Ext", "Calls", "Avg sentiment", "Talk time"],
        rows: [...agents]
          .sort((a, b) => b.avg_sentiment - a.avg_sentiment)
          .map((a) => [
            a.name,
            a.extension,
            a.calls,
            Math.round(a.avg_sentiment),
            formatMinutes(a.talk_minutes),
          ]),
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 12, right: 64, bottom: 18, left: -4 }}>
          <CartesianGrid stroke={chart.grid} />
          <XAxis
            type="number"
            dataKey="calls"
            name="Calls"
            {...axisProps}
            label={{
              value: "Calls",
              position: "insideBottom",
              offset: -12,
              fill: chart.tick,
              fontSize: 12,
            }}
          />
          <YAxis
            type="number"
            dataKey="avg_sentiment"
            name="Sentiment"
            domain={[0, 100]}
            ticks={[0, 20, 40, 60, 80, 100]}
            {...axisProps}
          />
          <ZAxis type="number" dataKey="talk_minutes" range={[60, 520]} />
          <ReferenceLine y={averageSentiment} stroke="rgba(255,255,255,0.25)" />
          <ReferenceLine x={avgCalls} stroke="rgba(255,255,255,0.25)" />
          <Tooltip
            cursor={false}
            content={
              <ChartTooltip
                title={(_, payload) => payload[0].payload.name}
                rows={(payload) => {
                  const a = payload[0].payload;
                  return [
                    { label: "Extension", value: a.extension },
                    { label: "Team", value: a.team },
                    { label: "Calls", value: a.calls },
                    {
                      label: "Avg sentiment",
                      value: Math.round(a.avg_sentiment),
                      color: sentimentScale[sentimentRating(a.avg_sentiment)],
                    },
                    {
                      label: "Talk time",
                      value: formatMinutes(a.talk_minutes),
                    },
                  ];
                }}
              />
            }
          />
          <Scatter
            data={data}
            fillOpacity={0.85}
            stroke={chart.surface}
            strokeWidth={2}
          >
            {data.map((a) => (
              <Cell
                key={a.id}
                fill={sentimentScale[sentimentRating(a.avg_sentiment)]}
              />
            ))}
            <LabelList
              dataKey="label"
              position="right"
              fill={chart.tick}
              fontSize={12}
              offset={10}
            />
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export default AgentScatter;
